<template lang="pug">
  .product-tabs(ref="container")
    Tabs.tab-select(
      @update:modelValue="onInput($event)"
      :modelValue="show"
      :tabs="tabs.map(t => t.props.tab)"
      :sticky="sticky"
    )
    .tab-content-container
      .product-tab(
        v-for="tab in tabsWithSlots"
        :class="show !== tab.props.tab && 'inactive'"
      )
        h2.tab-heading.heading-h5(@click="showInSidebar = tab")
          | {{__(tab.props.tab)}}
          Icon(v-if="!tab.slots.mobileDescription && !tab.props.description" name="arrow-right")
        .tab-description(v-if="(tab.slots.mobileDescription || []).length || tab.props.description")
          RenderBlock(
            v-if="(tab.slots.mobileDescription || []).length"
            :block="tab.slots.mobileDescription"
            :product="product"
            :selectedSimple="selectedSimple"
          )
          p.desc(v-else) {{tab.props.description}}
          .more.heading-h5(@click="showInSidebar = tab")
            | {{__('Read More')}}
            Icon(name="arrow-right")
        .tab-content
          RenderBlock(:block="tab" :product="product" :selectedSimple="selectedSimple")
    Drawer(v-if="showInSidebar" @hide="showInSidebar = false" :title="showInSidebar.props.tab")
      .drawer-body
        RenderBlock(:block="showInSidebar" :product="product"  :selectedSimple="selectedSimple")
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, type PropType } from 'vue'
import { useRogue } from '../../../js/rogue'
import { useMQ } from '../../../js/mq'

type AnyRecord = Record<string, any>

const props = defineProps({
  active: {
    type    : Number,
    default : 0,
  },
  product: {
    type    : Object as PropType<AnyRecord>,
    default : () => ({}),
  },
  selectedSimple: {
    type    : Object as PropType<AnyRecord>,
    default : () => ({}),
  },
  tabs: {
    type    : Array as PropType<any[]>,
    default : () => ([]),
  },
  sticky: {
    type    : Boolean,
    default : false,
  },
})

const container = ref<HTMLElement | null>(null)
const show = ref<string>('')
const showInSidebar = ref<any | undefined>(undefined)

const tabsWithSlots = computed(() => props.tabs.filter((tab: any) => tab.slots))

watch(() => (props.product as AnyRecord)?.productId, (newVal, oldVal) => {
  if (newVal && oldVal) {
    show.value = props.tabs[props.active]?.props?.tab
  }
})

// Initialize default tab immediately
if (props.tabs.length && !show.value) {
  show.value = props.tabs[props.active]?.props?.tab
}

const rogue = useRogue()
const mq = useMQ()

onMounted(() => {
  const hash = (rogue.router?.currentRoute.value?.hash || '').slice(1)
  if (!hash) return

  const sanitize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const tab = props.tabs.find((t: any) => sanitize(t.props.tab) === sanitize(hash))

  if (tab) {
    if (mq.value === 'mobile') {
      showInSidebar.value = tab
    } else {
      show.value = tab.props.tab
    }
  }
})

function onInput(event: string) {
  show.value = event
  if (props.sticky) {
    const el = container.value
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
</script>

<style lang="scss" scoped>

@include mobile() {
  .product-tab {
    padding: $space-s;
    padding-bottom: 0;
    border-top: 1px solid $color-gray;

    &:last-child {
      border-bottom: 1px solid $color-gray;
    }
  }

  .tab-heading {
    display: flex;
    justify-content: space-between;
    cursor: pointer;
  }

  .more {
    margin-top: $space-s;
    display: flex;
    cursor: pointer;
    align-items: center;
  }

  .icon {
    width: 12px;
    margin-left: $space-xxs;
  }

  .tab-select, .tab-content {
    display: none;
  }
}

@include not-mobile() {
  .tab-content-container {
    padding: $space-s;
  }

  .tab-heading, .tab-description, .product-tab.inactive {
    display: none;
  }
}

.drawer-body {
  padding: $space-s;
}
</style>
